import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

type ButtonVariant = "primary" | "green" | "red" | "orange" | "outline" | "ghost" | "secondary" | "ssecondary" | "upload" | "pill" | "unstyled";

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "children"> & {
    type?: "button" | "submit" | "reset";
    variant?: ButtonVariant;
    disabled?: boolean;
    className?: string;
    selected?: boolean;
    icon?: ReactNode;
    dotClassName?: string;
    analyticsId?: string;
    children?: ReactNode;
};

const baseClass = "font-bold w-full h-14 rounded-md border shadow-none";
const interactionClass = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#005fb8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
const primaryClass = "border-transparent bg-[#005FB8] text-white hover:bg-[#004985]";
const greenClass = "bg-emerald-700 hover:bg-emerald-800 text-white";
const redClass = "bg-red-500 hover:bg-red-600 text-white";
const orangeClass = "bg-orange-500 hover:bg-orange-600 text-white";
const outlineClass = "border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
const uploadClass = "border-[#005FB8] bg-[#005FB8] text-white hover:bg-[#004985]";
const ghostClass = "border-transparent bg-transparent text-slate-400 hover:bg-slate-100";
const ssecondaryClass = "border-transparent bg-slate-600 text-white hover:bg-slate-700";
const disabledClass = "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed pointer-events-none";
const pillBaseClass = "inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-bold shadow-sm hover:brightness-95";
const pillDisabledClass = "!border-slate-200 !bg-slate-100 !text-slate-400 cursor-not-allowed pointer-events-none shadow-none";
const pillSelectedClass = "";
const pillUnselectedClass = "!border-slate-200 !bg-white !text-black hover:!bg-slate-50";

const variantClasses: Record<ButtonVariant, string> = {
    primary: primaryClass,
    green: greenClass,
    red: redClass,
    orange: orangeClass,
    outline: outlineClass,
    upload: uploadClass,
    ghost: ghostClass,
    secondary: ssecondaryClass,
    ssecondary: ssecondaryClass,
    pill: "",
    unstyled: "",
};

function getButtonClassName(variant: ButtonVariant | undefined, disabled: boolean | undefined) {
    if (disabled) return disabledClass;
    if (!variant) return "";
    return variantClasses[variant];
}

function getPillButtonClassName(selected: boolean | undefined, className: string, disabled: boolean | undefined) {
    return `${pillBaseClass} ${className} ${disabled ? pillDisabledClass : pillVariantClass(selected)}`.trim();
}

function pillVariantClass(selected: boolean | undefined) {
    return selected ? `${pillSelectedClass} border-transparent bg-[#005fb8] text-white` : pillUnselectedClass;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
    const { type = "button", variant, disabled, className = "", selected, icon, dotClassName, analyticsId, children, ...buttonProps } = props;

    if (variant === "pill") {
        return (
            <button {...buttonProps} ref={ref} type={type} className={`${getPillButtonClassName(selected, className, disabled)} ${interactionClass}`.trim()} disabled={disabled} data-analytics-id={analyticsId}>
                {icon ?? (dotClassName ? <span className={`h-2 w-2 rounded-full ${dotClassName}`} /> : null)}
                <span>{children}</span>
            </button>
        );
    }

    if (variant === "unstyled") {
        return <button {...buttonProps} ref={ref} type={type} className={`${interactionClass} ${className}`.trim()} disabled={disabled} data-analytics-id={analyticsId}>{children}</button>;
    }

    const variantClass = getButtonClassName(variant, disabled);

    return (
        <button {...buttonProps} ref={ref} type={type} className={`${baseClass} ${interactionClass} ${variantClass} ${className}`.trim()} disabled={disabled} data-analytics-id={analyticsId}>
            {children}
        </button>
    );
});

export default Button;
